import { useState } from "react";
import { useAuth } from "../../lib/auth";
import { useIntl } from "../../lib/i18n";
import { useFetch } from "../../lib/useFetch";
import { api } from "../../lib/api";

// Backend POST /tenant/reviews returns a JSON array (not a wrapper).
// Each row has approval + pin flags as booleans, not timestamps.
type Review = {
  id: string;
  author: string;
  body: string;
  rating: number;
  source: string;
  approved: boolean;
  pinned: boolean;
};

// Some legacy callers return {reviews: [...]}; tolerate both shapes.
type ReviewsResponse = Review[] | { reviews: Review[] };

function asRows(data: ReviewsResponse | null): Review[] | null {
  if (data === null) return null;
  if (Array.isArray(data)) return data;
  return data.reviews ?? [];
}

export default function ReviewsPage() {
  const { t } = useIntl();
  const { session } = useAuth();
  const { data, loading, error, refresh } =
    useFetch<ReviewsResponse>("/api/v1/tenant/reviews");
  const rows = asRows(data);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const act = async (id: string, action: "approve" | "unapprove" | "pin") => {
    setPendingId(id);
    setActionError(null);
    try {
      const res = await api(
        `/api/v1/tenant/reviews/${encodeURIComponent(id)}/${action}`,
        {
          method: "POST",
          token: session?.token,
          body: JSON.stringify({}),
        },
      );
      if (!res.ok && res.status !== 409) {
        throw new Error((await res.text().catch(() => res.statusText)) || res.statusText);
      }
      refresh();
    } catch (e) {
      setActionError(String((e as Error).message ?? e));
    } finally {
      setPendingId(null);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-semibold text-brand-900">{t("reviews.title")}</h1>
      {actionError && <p className="mt-2 text-sm text-rose-600">{actionError}</p>}
      {loading && <p className="mt-4 text-slate-500">…</p>}
      {error && <p className="mt-4 text-red-600">{error}</p>}
      {!loading && !error && (rows === null || rows.length === 0) && (
        <p className="mt-4 text-slate-500">{t("reviews.empty")}</p>
      )}
      {!loading && !error && rows && rows.length > 0 && (
        <div className="mt-4 overflow-x-auto rounded-md border border-slate-200 bg-white">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-slate-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide">
                  {t("reviews.col.author")}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide">
                  {t("reviews.col.rating")}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide">
                  {t("reviews.col.source")}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide">
                  {t("reviews.col.status")}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide">
                  {t("reviews.col.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => {
                const busy = pendingId === r.id;
                return (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-700">{r.author}</td>
                    <td className="px-4 py-3 text-slate-700">{"★".repeat(r.rating)}</td>
                    <td className="px-4 py-3 text-slate-700">{r.source}</td>
                    <td className="px-4 py-3 text-slate-700">
                      {r.approved
                        ? t("reviews.status.approved")
                        : t("reviews.status.pending")}
                      {r.pinned && (
                        <span className="ml-2 rounded bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700 ring-1 ring-brand-200">
                          {t("reviews.status.pinned")}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        {!r.approved && (
                          <button
                            type="button"
                            onClick={() => act(r.id, "approve")}
                            disabled={busy}
                            className="rounded-md bg-emerald-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
                          >
                            {t("reviews.action.approve")}
                          </button>
                        )}
                        {r.approved && (
                          <button
                            type="button"
                            onClick={() => act(r.id, "unapprove")}
                            disabled={busy}
                            className="rounded-md bg-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-300 disabled:opacity-50"
                          >
                            {t("reviews.action.unapprove")}
                          </button>
                        )}
                        {!r.pinned && (
                          <button
                            type="button"
                            onClick={() => act(r.id, "pin")}
                            disabled={busy}
                            className="rounded-md bg-brand-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-500 disabled:opacity-50"
                          >
                            {t("reviews.action.pin")}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
